/**
 * <article-featured-product>
 * ---------------------------------------------------------------------------------------------------------------------
 * Lightweight variant switcher for a single featured product embedded inside a blog article.
 *
 * The theme's <variant-picker> updates a product by re-fetching the *current* section via the Section Rendering API
 * (`/products/{handle}?section_id=...`). Inside a blog article the current section is `main-article`, which renders
 * empty on a product URL (there is no `article` object), so that flow cannot be reused here. Instead we resolve the
 * selected variant from embedded variant JSON and update the price, add-to-cart target, option labels, view-details
 * link and gallery ourselves. The product gallery reacts to the standard `variant:change` event it already listens for.
 * ---------------------------------------------------------------------------------------------------------------------
 */
(function () {
  if (customElements.get('article-featured-product')) return;

  class ArticleFeaturedProduct extends HTMLElement {
    connectedCallback() {
      this.formId = this.getAttribute('form-id');
      this.form = this.formId ? document.getElementById(this.formId) : null;

      const dataEl = this.querySelector('[data-article-featured-variants]');
      try {
        this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];
      } catch (error) {
        this.variants = [];
      }

      this.currentVariant =
        this.variants.find((variant) => variant.id === this._initialVariantId()) || this.variants[0] || null;

      this._onChange = this._onChange.bind(this);
      this.addEventListener('change', this._onChange);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this._onChange);
    }

    _initialVariantId() {
      const idInput = this.form && this.form.querySelector('[name="id"]');
      return idInput ? parseInt(idInput.value, 10) : null;
    }

    _onChange(event) {
      if (!event.target.matches('input[type="radio"][data-option-position]')) return;
      this._selectVariant();
    }

    /** Currently checked option value names, ordered by option position. */
    _selectedOptionNames() {
      return Array.from(this.querySelectorAll('input[type="radio"][data-option-position]:checked'))
        .sort(
          (a, b) => parseInt(a.getAttribute('data-option-position'), 10) - parseInt(b.getAttribute('data-option-position'), 10)
        )
        .map((input) => input.getAttribute('data-option'));
    }

    _findVariant(optionNames) {
      return (
        this.variants.find((variant) => optionNames.every((name, index) => variant.options[index] === name)) || null
      );
    }

    _selectVariant() {
      const optionNames = this._selectedOptionNames();
      const previousVariant = this.currentVariant;
      const variant = this._findVariant(optionNames);
      this.currentVariant = variant;

      this._updateSelectedLabels(optionNames);
      this._updateIdInput(variant);
      this._updatePrice(variant);
      this._updateBuyButton(variant);
      this._updateViewDetails(variant);
      this._updateWishlist(variant);

      if (this.form) {
        this.form.dispatchEvent(
          new CustomEvent('variant:change', {
            bubbles: true,
            detail: { formId: this.formId, variant, previousVariant },
          })
        );
      }
    }

    _updateSelectedLabels(optionNames) {
      optionNames.forEach((name, index) => {
        const label = this.querySelector('[data-selected-option-value="' + (index + 1) + '"]');
        if (label) label.textContent = name;
      });
    }

    _updateIdInput(variant) {
      if (!this.form || !variant) return;
      const idInput = this.form.querySelector('[name="id"]');
      if (idInput) idInput.value = variant.id;
    }

    /* Keep the wishlist heart pointed at the selected variant, and reflect its saved state. */
    _updateWishlist(variant) {
      if (!variant) return;
      const heart = this.querySelector('.swym-inline-wishlist');
      if (!heart) return;
      heart.dataset.epi = variant.id;
      const saved = !!(window.__swymWishlistEpis && window.__swymWishlistEpis[variant.id]);
      heart.classList.toggle('is-added', saved);
      heart.classList.toggle('swym-added', saved);
    }

    _updatePrice(variant) {
      const container = this.querySelector('[data-article-featured-price]');
      if (!container || !variant) return;

      const template = this.querySelector('template[data-variant-price="' + variant.id + '"]');
      if (template) {
        container.innerHTML = '';
        container.appendChild(template.content.cloneNode(true));
      }
    }

    _updateBuyButton(variant) {
      if (!this.form) return;
      const button = this.form.querySelector('[name="add"]');
      if (!button) return;

      const content = button.querySelector('.button__content') || button;
      const available = Boolean(variant && variant.available);
      button.disabled = !available;

      let text;
      if (!variant) {
        text = this.getAttribute('data-unavailable-text');
      } else if (!available) {
        text = this.getAttribute('data-sold-out-text');
      } else {
        text = this.getAttribute('data-add-to-cart-text');
      }

      if (text) {
        content.textContent = text;
        if (content.hasAttribute('up-text')) content.setAttribute('up-text', text);
      }
    }

    _updateViewDetails(variant) {
      const link = this.querySelector('[data-view-details]');
      if (link && variant && variant.url) link.setAttribute('href', variant.url);
    }
  }

  customElements.define('article-featured-product', ArticleFeaturedProduct);
})();
